import { useEffect, useMemo, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import {
  useGetAllTypes,
  useUpdateTypeMutation,
} from "../../../../../api/jeep-rally/types/types.hooks";
import { isJeepRallyAdmin } from "../../Categories/utils/category.utils";
import TypeForm from "../_components/TypeForm";
import {
  buildTypeFormData,
  getTypeApiErrorMessage,
  initialTypeForm,
  mapTypeToForm,
  normalizeTypes,
  validateTypeForm,
} from "../utils/type.utils";

const TypeUpdatePage = () => {
  const history = useHistory();
  const { id } = useParams();
  const canManage = isJeepRallyAdmin();
  const locationType = history.location.state?.type || null;

  const [form, setForm] = useState(
    locationType ? mapTypeToForm(locationType) : initialTypeForm
  );
  const [initialForm, setInitialForm] = useState(
    locationType ? mapTypeToForm(locationType) : initialTypeForm
  );
  const [validationErrors, setValidationErrors] = useState({});

  const {
    data: typesData,
    isLoading: isTypesLoading,
    isError: isTypesError,
    error: typesError,
  } = useGetAllTypes();

  const types = useMemo(() => normalizeTypes(typesData), [typesData]);

  const type = useMemo(() => {
    return (
      types.find((item) => (item._id || item.id) === id) ||
      (locationType && (locationType._id || locationType.id) === id
        ? locationType
        : null)
    );
  }, [types, id, locationType]);

  const {
    mutate: updateType,
    isLoading,
    isPending,
    error,
    reset,
  } = useUpdateTypeMutation();

  const submitting = isLoading || isPending;

  useEffect(() => {
    if (!type) return;
    const nextForm = mapTypeToForm(type);
    setForm((prev) => ({
      ...nextForm,
      imageFile: prev.imageFile,
    }));
    setInitialForm(nextForm);
  }, [type]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setValidationErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canManage || !id) return;
    reset();

    const nextErrors = validateTypeForm(form, {
      existingTypes: types,
      currentId: id,
    });
    setValidationErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const formData = buildTypeFormData(form, {
      isEdit: true,
      initialForm,
    });

    if (!formData) {
      history.push("/jeep-rally/types");
      return;
    }

    updateType(
      { typeId: id, formData },
      {
        onSuccess: () => {
          history.push("/jeep-rally/types");
        },
      }
    );
  };

  const goBack = () => history.push("/jeep-rally/types");

  if (isTypesLoading && !locationType) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isTypesError && !locationType) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <Alert variant="danger" className="mb-0">
              {getTypeApiErrorMessage(
                typesError,
                "Failed to load type details."
              )}
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  if (!isTypesLoading && !type) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <Alert variant="danger" className="mb-0">
              Type not found.
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={goBack}
              aria-label="Back to types list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">Update Type</h4>
          </div>
        </div>

        <div className="card-body">
          <p className="text-muted mb-4">
            Update type details. Upload a new image to replace the current one.
            The key cannot be changed.
          </p>

          {!canManage && (
            <Alert variant="warning" className="mb-4">
              Admin access is required to update types.
            </Alert>
          )}

          {error && (
            <Alert variant="danger" className="mb-4">
              {getTypeApiErrorMessage(
                error,
                "Failed to update type. Please try again."
              )}
            </Alert>
          )}

          <TypeForm
            form={form}
            validationErrors={validationErrors}
            submitting={submitting}
            submitLabel="Update Type"
            submittingLabel="Updating..."
            isEditMode
            readOnly={!canManage}
            onSubmit={handleSubmit}
            onFieldChange={updateField}
            onBack={goBack}
          />
        </div>
      </div>
    </div>
  );
};

export default TypeUpdatePage;
